import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';

// Local image paths
const LOGO_URL = '/images/TCA_Logo.png';
const BANNER_URL = '/images/TCA_Banner.png';

const AdminLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Login flow states
  const [step, setStep] = useState('login'); // login, mfa, mfa-setup, signup, reset-password, new-password
  const [isLoading, setIsLoading] = useState(false);
  
  // Form fields
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [tempPassword, setTempPassword] = useState('');
  
  // Tokens
  const [sessionToken, setSessionToken] = useState('');
  const [setupToken, setSetupToken] = useState('');
  
  // MFA Setup
  const [qrCode, setQrCode] = useState('');
  const [mfaSecret, setMfaSecret] = useState('');
  
  // Signup
  const [signupEmail, setSignupEmail] = useState('');
  
  // Modal states
  const [showResetModal, setShowResetModal] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');

  // Check for setup token in URL (for approved signups)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    const email = params.get('email');
    
    if (token && email) {
      setSetupToken(token);
      setUsername(email);
      setStep('setup-password');
    }
  }, [location]);

  // Check if already authenticated
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      verifyToken(token);
    }
  }, []);

  const verifyToken = async (token) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/check-auth`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        navigate('/admin');
      } else {
        localStorage.removeItem('admin_token');
      }
    } catch (error) {
      localStorage.removeItem('admin_token');
    }
  };

  // Step 1: Login with username/password
  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }
      
      if (data.mfa_required) {
        setSessionToken(data.session_token);
        setStep('mfa');
        toast.success('Password verified. Please enter MFA code.');
      } else if (data.mfa_setup_required) {
        setSetupToken(data.setup_token);
        // Request MFA setup
        await initiateMfaSetup(data.setup_token);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Verify MFA code
  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/verify-mfa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          username, 
          mfa_code: mfaCode,
          session_token: sessionToken 
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'MFA verification failed');
      }
      
      if (data.password_reset_required) {
        setSetupToken(data.reset_token);
        setStep('new-password');
        toast.info('Please set a new password.');
      } else {
        // Success! Store token and redirect
        localStorage.setItem('admin_token', data.access_token);
        toast.success('Login successful!');
        navigate('/admin');
      }
    } catch (error) {
      toast.error(error.message);
      setMfaCode('');
    } finally {
      setIsLoading(false);
    }
  };

  // Initiate MFA setup
  const initiateMfaSetup = async (token) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/setup-mfa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, setup_token: token })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'MFA setup failed');
      }
      
      setQrCode(data.qr_code);
      setMfaSecret(data.secret);
      setSetupToken(token);
      setStep('mfa-setup');
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Verify MFA setup
  const handleMfaSetupVerify = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/verify-mfa-setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          username, 
          setup_token: setupToken,
          mfa_code: mfaCode 
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'MFA setup verification failed');
      }
      
      localStorage.setItem('admin_token', data.access_token);
      toast.success('MFA setup complete! Welcome to the admin dashboard.');
      navigate('/admin');
    } catch (error) {
      toast.error(error.message);
      setMfaCode('');
    } finally {
      setIsLoading(false);
    }
  };

  // Password Reset Request
  const handleResetRequest = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/reset-password-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: resetEmail })
      });
      
      const data = await response.json();
      
      toast.success('If this email is registered, a temporary password has been sent.');
      setShowResetModal(false);
      setResetEmail('');
    } catch (error) {
      toast.error('Failed to process request');
    } finally {
      setIsLoading(false);
    }
  };

  // Set New Password
  const handleSetNewPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/set-new-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          username, 
          temp_password: tempPassword,
          new_password: newPassword 
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to set password');
      }
      
      toast.success('Password updated! Please login with your new password.');
      setStep('login');
      setPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTempPassword('');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Signup Request
  const handleSignupRequest = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/signup-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: signupEmail })
      });
      
      const data = await response.json();
      
      toast.success('Signup request submitted. You will be notified once approved.');
      setShowSignupModal(false);
      setSignupEmail('');
    } catch (error) {
      toast.error('Failed to submit signup request');
    } finally {
      setIsLoading(false);
    }
  };

  // Account Setup (for approved signups)
  const handleSetupPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/setup-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          username, 
          setup_token: setupToken,
          password: newPassword 
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Account setup failed');
      }
      
      // Now initiate MFA setup
      await initiateMfaSetup(data.setup_token);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex flex-col items-center justify-center px-4 py-8">
      {/* Logo */}
      <div className="mb-6 flex flex-col items-center gap-3">
        <img src={LOGO_URL} alt="TCA Auditors Logo" className="h-16 sm:h-20 w-auto object-contain" />
        <img src={BANNER_URL} alt="Tax & Auditing Consultants" className="h-10 sm:h-14 w-auto object-contain" />
      </div>

      <Card className="w-full max-w-md shadow-lg">
        <CardContent className="p-6 sm:p-8">
          {/* Login Step */}
          {step === 'login' && (
            <>
              <h2 className="text-xl font-bold text-center text-gray-900 mb-6">Admin Login</h2>
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full p-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-tca-blue"
                    placeholder="Please input your email address"
                    required
                    data-testid="admin-login-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full p-3 pr-11 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-tca-blue"
                      placeholder="Please input your password"
                      required
                      data-testid="admin-login-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      data-testid="admin-login-password-toggle"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                <Button 
                  type="submit" 
                  disabled={isLoading}
                  className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold py-3"
                  data-testid="admin-login-submit"
                >
                  {isLoading ? 'Verifying...' : 'Login'}
                </Button>
              </form>
              
              <div className="mt-4 flex flex-col gap-2">
                <button
                  onClick={() => setShowResetModal(true)}
                  className="text-sm text-accent hover:underline"
                  data-testid="admin-reset-password-btn"
                >
                  Reset Password
                </button>
                <button
                  onClick={() => setShowSignupModal(true)}
                  className="text-sm text-gray-500 hover:underline"
                  data-testid="admin-signup-btn"
                >
                  Request Access
                </button>
              </div>
            </>
          )}

          {/* MFA Verification Step */}
          {step === 'mfa' && (
            <>
              <h2 className="text-xl font-bold text-center text-gray-900 mb-2">Two-Factor Authentication</h2>
              <p className="text-sm text-gray-600 text-center mb-6">
                Enter the 6-digit code from your authenticator app
              </p>
              <form onSubmit={handleMfaVerify} className="space-y-4">
                <div>
                  <input
                    type="text"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full p-4 border-2 rounded-lg text-center text-2xl tracking-widest font-mono focus:outline-none focus:ring-2 focus:ring-tca-blue"
                    placeholder="000000"
                    maxLength={6}
                    required
                    autoFocus
                    data-testid="admin-mfa-code"
                  />
                </div>
                <Button 
                  type="submit" 
                  disabled={isLoading || mfaCode.length !== 6}
                  className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold py-3"
                  data-testid="admin-mfa-submit"
                >
                  {isLoading ? 'Verifying...' : 'Verify Code'}
                </Button>
              </form>
              <button
                onClick={() => { setStep('login'); setMfaCode(''); }}
                className="mt-4 text-sm text-gray-500 hover:underline w-full text-center"
              >
                Back to Login
              </button>
            </>
          )}

          {/* MFA Setup Step */}
          {step === 'mfa-setup' && (
            <>
              <h2 className="text-xl font-bold text-center text-gray-900 mb-2">Set Up Two-Factor Authentication</h2>
              <p className="text-sm text-gray-600 text-center mb-4">
                Scan this QR code with Google Authenticator or Authy
              </p>
              
              {qrCode && (
                <div className="flex justify-center mb-4">
                  <img src={qrCode} alt="MFA QR Code" className="w-48 h-48" />
                </div>
              )}
              
              <p className="text-xs text-gray-500 text-center mb-4">
                Or enter this code manually: <code className="bg-gray-100 px-2 py-1 rounded">{mfaSecret}</code>
              </p>
              
              <form onSubmit={handleMfaSetupVerify} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Enter the 6-digit code to verify setup
                  </label>
                  <input
                    type="text"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full p-4 border-2 rounded-lg text-center text-2xl tracking-widest font-mono focus:outline-none focus:ring-2 focus:ring-tca-blue"
                    placeholder="000000"
                    maxLength={6}
                    required
                    data-testid="admin-mfa-setup-code"
                  />
                </div>
                <Button 
                  type="submit" 
                  disabled={isLoading || mfaCode.length !== 6}
                  className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold py-3"
                  data-testid="admin-mfa-setup-submit"
                >
                  {isLoading ? 'Verifying...' : 'Complete Setup'}
                </Button>
              </form>
            </>
          )}

          {/* Setup Password (for approved signups) */}
          {step === 'setup-password' && (
            <>
              <h2 className="text-xl font-bold text-center text-gray-900 mb-2">Set Up Your Account</h2>
              <p className="text-sm text-gray-600 text-center mb-6">
                Create a password for {username}
              </p>
              <form onSubmit={handleSetupPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full p-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-tca-blue"
                    placeholder="Min 8 characters"
                    required
                    minLength={8}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full p-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-tca-blue"
                    placeholder="Confirm password"
                    required
                  />
                </div>
                <Button 
                  type="submit" 
                  disabled={isLoading}
                  className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold py-3"
                >
                  {isLoading ? 'Setting up...' : 'Set Password & Continue'}
                </Button>
              </form>
            </>
          )}

          {/* Set New Password (after temp password) */}
          {step === 'new-password' && (
            <>
              <h2 className="text-xl font-bold text-center text-gray-900 mb-2">Set New Password</h2>
              <p className="text-sm text-gray-600 text-center mb-6">
                Please create a new password to continue
              </p>
              <form onSubmit={handleSetNewPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Temporary Password</label>
                  <input
                    type="password"
                    value={tempPassword}
                    onChange={(e) => setTempPassword(e.target.value)}
                    className="w-full p-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-tca-blue"
                    placeholder="From your email"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full p-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-tca-blue"
                    placeholder="Min 8 characters"
                    required
                    minLength={8}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full p-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-tca-blue"
                    placeholder="Confirm password"
                    required
                  />
                </div>
                <Button 
                  type="submit" 
                  disabled={isLoading}
                  className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold py-3"
                >
                  {isLoading ? 'Updating...' : 'Update Password'}
                </Button>
              </form>
              <button
                onClick={() => setStep('login')}
                className="mt-4 text-sm text-gray-500 hover:underline w-full text-center"
              >
                Back to Login
              </button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Reset Password Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Reset Password</h3>
              <p className="text-sm text-gray-600 mb-4">
                Enter your email address to receive a temporary password.
              </p>
              <form onSubmit={handleResetRequest} className="space-y-4">
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="w-full p-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-tca-blue"
                  placeholder="Please input your email address"
                  required
                  data-testid="admin-reset-email"
                />
                <div className="flex gap-2">
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={() => { setShowResetModal(false); setResetEmail(''); }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={isLoading}
                    className="flex-1 bg-accent hover:bg-accent/90 text-accent-foreground font-semibold"
                    data-testid="admin-reset-submit"
                  >
                    {isLoading ? 'Sending...' : 'Send Password'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Signup Request Modal */}
      {showSignupModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Request Admin Access</h3>
              <p className="text-sm text-gray-600 mb-4">
                Enter your email address. Your request will be sent to the administrator for approval.
              </p>
              <form onSubmit={handleSignupRequest} className="space-y-4">
                <input
                  type="email"
                  value={signupEmail}
                  onChange={(e) => setSignupEmail(e.target.value)}
                  className="w-full p-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-tca-blue"
                  placeholder="Please input your email address"
                  required
                  data-testid="admin-signup-email"
                />
                <div className="flex gap-2">
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={() => { setShowSignupModal(false); setSignupEmail(''); }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={isLoading}
                    className="flex-1 bg-accent hover:bg-accent/90 text-accent-foreground font-semibold"
                    data-testid="admin-signup-submit"
                  >
                    {isLoading ? 'Submitting...' : 'Request Access'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Footer */}
      <p className="mt-6 text-xs text-gray-400">
        © {new Date().getFullYear()} TCA Auditors. Secure Admin Portal.
      </p>
    </div>
  );
};

export default AdminLogin;
